import React from 'react';
import { inject, observer } from 'mobx-react';
import { Alert, Row } from 'reactstrap';
import { defineMessages, FormattedMessage } from 'react-intl';
import { uniqueId } from 'lodash';
import PropTypes from 'prop-types';

import { modelOf } from '../../../prop-types';
import globalTranslations from '../../../i18n/globalTranslations';
import CustomCol from '../../bootstrap/CustomCol';
import Icon from '../../common/Icon';
import OrderTotalClass from '../../../types/OrderTotalClass';
import Price from '../../product/Price';
import ConfigStore from '../../../store/ConfigStore';
import CurrentOrder from '../../../models/cart/CurrentOrder';
import OrderType from '../../../types/OrderType';

const messages = defineMessages({
  paymentInstructions: {
    id: 'checkout.paymentInformation',
    defaultMessage:
      'Confirmation email has also been sent to the email address {email}. Make sure email address is correct.',
  },
  paymentInstructions2: {
    id: 'checkout.paymentInformation2',
    defaultMessage:
      "If you don't see the message, be sure to check your spam folder as well.",
  },
  shoppingCenterMerchantInfo: {
    id: 'checkout.shoppingCenterMerchantInfo',
    defaultMessage: "merchant's {merchant}",
  },
  trackOrder: {
    id: 'checkout.trackOrder',
    defaultMessage:
      'You can track your order by clicking {link}, you can also get to the {merchantInfo} order tracking page from the confirmation email.',
  },
});

const CheckoutConfirmation = ({ configStore, currentOrder, orderType }) => {
  const renderSuccessPage = () => {
    if (!currentOrder) {
      return null;
    }

    return (
      <CustomCol xs={12} md={10} xxl={7} className="CheckoutConfirmation">
        {renderConfirmation()}
      </CustomCol>
    );
  };

  const renderConfirmation = () => {
    return (
      <>
        {renderSuccessInformation()}
        {configStore.account.enableMyAccount && renderTrackingInformation()}
        {currentOrder.has_licensed_products &&
          renderAlert(
            'warning',
            <FormattedMessage
              id="checkout.successfulPurchaseLicensedProductsSentence"
              defaultMessage="Some products in your order require a license. You may pay your order now; after this, you are required to send the relevant licenses to us by email. Please refer to the shipping terms page for more details."
            />
          )}
        {renderInformation()}
      </>
    );
  };

  const renderSuccessInformation = () => {
    const { confirmation_email } = currentOrder;

    const successTitle =
      orderType === OrderType.PURCHASE ? (
        <FormattedMessage {...globalTranslations.successfulPurchasePageTitle} />
      ) : (
        <FormattedMessage
          {...globalTranslations.successfulProposalReceivedTitle}
        />
      );

    return (
      <>
        {renderContentRow({ xs: 12 }, <Icon name="check" />, {
          className: 'CheckoutConfirmation__order-success-icon',
        })}
        {renderContentRow({ xs: 12 }, successTitle, {
          className: 'CheckoutConfirmation__order-completed',
        })}
        {confirmation_email &&
          renderContentRow(
            { xs: 12 },
            <FormattedMessage
              {...messages.paymentInstructions}
              values={{
                email: confirmation_email,
              }}
            />,
            {
              className: 'CheckoutConfirmation__payment-instructions',
            }
          )}
        {confirmation_email &&
          renderContentRow(
            { xs: 12 },
            <FormattedMessage {...messages.paymentInstructions2} />,
            {
              className: 'CheckoutConfirmation__payment-instructions',
            }
          )}
      </>
    );
  };

  const renderTrackingInformation = () => {
    const paymentMethodMessages = currentOrder.payment_method_messages;
    const translationValues = getTranslationValues();

    return renderContentRow(
      { xs: 12 },
      <div className="CheckoutConfirmation__order-confirmation-message-track-order">
        {currentOrder.tracking_url && (
          <FormattedMessage
            {...messages.trackOrder}
            values={translationValues}
          />
        )}
        <div className="CheckoutConfirmation__payment-method-messages">
          {paymentMethodMessages &&
            paymentMethodMessages.length > 0 &&
            paymentMethodMessages.map((message, index) =>
              renderAlert('warning', <span>{message}</span>, {
                key: index,
              })
            )}
        </div>
      </div>,
      {
        className: 'CheckoutConfirmation__order-confirmation-message',
      }
    );
  };

  const getTranslationValues = () => {
    const merchant = (
      <FormattedMessage
        {...messages.shoppingCenterMerchantInfo}
        values={{
          merchant: currentOrder.merchant_name,
        }}
      />
    );

    const translationValues = {
      link: (
        <a href={currentOrder.tracking_url} target="_blank" rel="noopener">
          <FormattedMessage {...globalTranslations.hereSentence} />
        </a>
      ),
    };

    translationValues.merchantInfo = configStore.siteConfig.isShoppingCenter
      ? merchant
      : '';

    return translationValues;
  };

  const renderInformation = () => {
    const header =
      orderType === OrderType.PURCHASE ? (
        <FormattedMessage {...globalTranslations.orderInfoHeader} />
      ) : (
        <FormattedMessage {...globalTranslations.offerInfoHeader} />
      );
    const confirmation =
      orderType === OrderType.PURCHASE ? (
        <FormattedMessage {...globalTranslations.orderConfirmationThankYou} />
      ) : (
        <FormattedMessage {...globalTranslations.offerConfirmationThankYou} />
      );

    return (
      <>
        {renderContentRow({ xs: 12 }, header, {
          className: 'CheckoutConfirmation__order-information-title',
        })}
        {renderContentRow({ xs: 12 }, renderInformationContent())}
        {renderContentRow(
          { xs: 12 },
          <div className="CheckoutConfirmation__order-information-thankyou">
            {confirmation}
          </div>
        )}
      </>
    );
  };

  const renderInformationContent = () => {
    const { paymentMethod } = currentOrder;
    const { shippingMethod } = currentOrder;
    const id = currentOrder.id;
    const paymentMethodName = paymentMethod && paymentMethod.name;
    const shippingMethodName = shippingMethod && shippingMethod.name;
    const pickupPoint = currentOrder.pickupPoint || null;
    const orderNumberText =
      orderType === OrderType.PURCHASE ? (
        <FormattedMessage {...globalTranslations.orderNumber} />
      ) : (
        <FormattedMessage {...globalTranslations.offerNumber} />
      );

    return (
      <div className="CheckoutConfirmation__order-information-content">
        {renderContentRow(
          { xs: 12 },
          <div className="CheckoutConfirmation__order-information-product-wrapper">
            {currentOrder.products.map((product) => (
              <div
                key={product.id}
                className="CheckoutConfirmation__order-information-product"
              >
                <span>
                  {product.productCode} - {product.name} x{' '}
                  {product.totalQuantity}
                </span>
                <span className="CheckoutConfirmation__order-information-value">
                  {currentOrder.totals.length > 0 && (
                    <Price price={product.total_price} />
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
        {currentOrder.totals.length > 0 &&
          renderContentRow(
            { xs: 12 },
            <div className="CheckoutConfirmation__order-information-total-wrapper">
              {currentOrder.totals
                .filter(
                  (total) =>
                    total.class !== OrderTotalClass.EXTRA_TOTAL &&
                    total.class !== OrderTotalClass.PRODUCTS_TOTAL
                )
                .map((total) => (
                  <div
                    key={uniqueId(total.name)}
                    className="CheckoutConfirmation__order-information-total"
                  >
                    <span>{total.name}</span>
                    <span
                      className="CheckoutConfirmation__order-information-value"
                      dangerouslySetInnerHTML={{
                        __html: total.text,
                      }}
                    />
                  </div>
                ))}
            </div>
          )}
        {renderContentRow(
          { xs: 12 },
          <div className="CheckoutConfirmation__order-information-id">
            {orderNumberText}
            <span className="CheckoutConfirmation__order-information-value">
              {id}
            </span>
          </div>
        )}
        {paymentMethodName &&
          renderContentRow(
            { xs: 12 },
            <div className="CheckoutConfirmation__order-information-payment-method">
              <FormattedMessage {...globalTranslations.paymentMethod} />
              <span className="CheckoutConfirmation__order-information-value">
                {paymentMethodName}
              </span>
            </div>
          )}
        {shippingMethodName &&
          renderContentRow(
            { xs: 12 },
            <div className="CheckoutConfirmation__order-information-shipping-method">
              <FormattedMessage {...globalTranslations.shippingMethod} />
              <span className="CheckoutConfirmation__order-information-value">
                {shippingMethodName}
              </span>
            </div>
          )}
        {pickupPoint &&
          renderContentRow(
            { xs: 12 },
            <div className="CheckoutConfirmation__order-information-pickup-point">
              <FormattedMessage {...globalTranslations.pickupPointTitle} />
              <div>
                <div className="CheckoutConfirmation__order-information-value">
                  {pickupPoint.name}
                </div>
                <div className="CheckoutConfirmation__order-information-value">
                  {pickupPoint.address}
                </div>
              </div>
            </div>
          )}
      </div>
    );
  };

  const renderContentRow = (sizes, content, className, rest) => (
    <Row {...rest} {...className}>
      <CustomCol {...sizes}>{content}</CustomCol>
    </Row>
  );

  const renderAlert = (type, message, rest) => (
    <Alert {...rest} color={type}>
      {message}
    </Alert>
  );

  return <>{renderSuccessPage()}</>;
};

CheckoutConfirmation.propTypes = {
  configStore: modelOf(ConfigStore).isRequired,
  currentOrder: modelOf(CurrentOrder).isRequired,
  orderType: PropTypes.string,
};

export default inject('configStore')(observer(CheckoutConfirmation));
